import { v4 as uuid } from 'uuid';

import { AwardType } from '@occc-app/offerHelpers/types';
import { calcDateTime, parseDate, startOfDateTime } from '@occc-app/lib/date';
import { OfferFragment, PromocodesOfferType } from '@occc/graphql';
import { LimitTimePeriod, getAwardCountInitialValues } from '@occc/feat-limits';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import {
  getDefaultTimezone,
  transformAwardRestrictionsToFormValues,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';

import {
  PromoCodeGroupFormValues,
  PromocodesConfigureFormInitialValues,
  PromocodeFormValues,
  PromocodesTargetFormValues,
} from './promocodes-configuration.types';

const mapPromoCodeGroupsFromOffer = (offer?: OfferFragment): PromoCodeGroupFormValues[] => {
  const earningMechanism = offer?.earningMechanism;

  if (earningMechanism?.__typename !== 'EarningMechanismPromocodes') {
    return [];
  }

  return earningMechanism.promoCodeGroups.edges.map(({ node }) => node);
};

export const getPromocodesAwardType = (
  promocodes: PromocodesConfigureFormInitialValues['promocodes'],
): AwardType => {
  const awardType = promocodes?.awardType as AwardType | undefined;

  if (
    awardType === AwardType.AwardPoints ||
    awardType === AwardType.AwardVouchers ||
    awardType === AwardType.AwardNullable
  ) {
    return awardType;
  }

  return AwardType.AwardNullable;
};

export const getPromocodeInitialValues = (): PromocodeFormValues => ({
  id: uuid(),
  code: '',
  description: null,
  startDate: startOfDateTime(calcDateTime(new Date(), 'add', 1, 'day'), 'day'),
  endDate: null,
  isSaved: false,
});

export const getDefaultGlobalPromocodesFields = (
  promocodes?: PromocodeFormValues[],
): NonNullable<PromocodesTargetFormValues['promocodes']> => ({
  codesType: PromocodesOfferType.Global,
  promocodes: promocodes ?? [],
  awardType: AwardType.AwardNullable,
});

const getOfferConfigureFormInitialValues = (): PromocodesConfigureFormInitialValues => ({
  earningMechanism: {},
  fulfillmentWindow: {
    isEnabled: false,
    timezone: getDefaultTimezone(),
  },
  awardCount: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
  },
  promocodes: {
    awardType: AwardType.AwardNullable,
    awardVouchers: {
      isEnabled: false,
      voucherCount: 1,
    },
  },
  awardRestrictions: { isEnabled: false, awards: [] },
});

const mapPromocodesFromEarningMechanism = (
  earningMechanism: Extract<
    OfferFragment['earningMechanism'],
    { __typename: 'EarningMechanismPromocodes' }
  >,
): PromocodeFormValues[] =>
  earningMechanism.promocodes.map((promocode) => ({
    isSaved: true,
    id: promocode.id,
    code: promocode.code,
    description: promocode.description || null,
    startDate: parseDate(promocode.startDate),
    endDate: promocode.endDate ? parseDate(promocode.endDate) : null,
    redemptions: promocode.stats?.redemptions ?? 0,
  }));

const transformOfferEarningMechanismAward = (
  earningMechanism: OfferFragment['earningMechanism'],
): Partial<PromocodesConfigureFormInitialValues> => {
  if (!earningMechanism || !earningMechanismHelper.isPromocodes(earningMechanism)) {
    return {};
  }

  const award = earningMechanism.award;

  if (award && awardHelper.isPointsAward(award)) {
    return {
      promocodes: {
        awardType: AwardType.AwardPoints,
        awardPoints: { points: award.points },
      },
    };
  }

  if (award && awardHelper.isVouchersAward(award)) {
    if (award.voucherExpirationDays) {
      return {
        promocodes: {
          awardType: AwardType.AwardVouchers,
          awardVouchers: {
            voucheredOffer: award.voucheredOffer,
            voucherCount: award.voucherCount,
            voucherExpirationDays: award.voucherExpirationDays,
            isEnabled: true as const,
          },
        },
      };
    }

    return {
      promocodes: {
        awardType: AwardType.AwardVouchers,
        awardVouchers: {
          voucheredOffer: award.voucheredOffer,
          voucherCount: award.voucherCount,
          isEnabled: false as const,
        },
      },
    };
  }

  return {
    promocodes: {
      awardType: AwardType.AwardNullable,
    },
  };
};

const transformOfferEarningMechanismTarget = (
  earningMechanism: OfferFragment['earningMechanism'],
): Pick<PromocodesConfigureFormInitialValues, 'promocodes'> | undefined => {
  if (!earningMechanism || !earningMechanismHelper.isPromocodes(earningMechanism)) {
    return undefined;
  }

  const { codesType } = earningMechanism;

  if (codesType === PromocodesOfferType.Individual) {
    return {
      promocodes: {
        codesType,
        awardType: AwardType.AwardNullable,
      },
    };
  }

  return {
    promocodes: getDefaultGlobalPromocodesFields(
      mapPromocodesFromEarningMechanism(earningMechanism),
    ),
  };
};

export const getPromocodesTargetInitialValues = (
  offer?: OfferFragment | null,
): PromocodesTargetFormValues => {
  if (!offer) {
    return {
      promoCodeGroups: [],
      promocodes: getDefaultGlobalPromocodesFields(),
    };
  }

  return {
    promoCodeGroups: mapPromoCodeGroupsFromOffer(offer),
    ...(transformOfferEarningMechanismTarget(offer.earningMechanism) ?? {
      promocodes: getDefaultGlobalPromocodesFields(),
    }),
  };
};

export const transformOfferToInitialValues = (
  offer?: OfferFragment,
): PromocodesConfigureFormInitialValues => {
  const values = getOfferConfigureFormInitialValues();

  if (!offer) {
    return values;
  }

  const awardRestrictionsConstraint = offer.constraints.find(
    (constraint) => constraint.__typename === 'ConstraintPurchasableEligibility',
  );

  const awardCount = getAwardCountInitialValues(
    offer,
    values.awardCount,
  ) as PromocodesConfigureFormInitialValues['awardCount'];

  return {
    ...values,
    ...getOfferNameAndDescriptionInitialValues(offer),
    ...transformOfferEarningMechanismAward(offer?.earningMechanism),
    ...transformOfferToFulfillmentWindowFormValue(offer),
    awardCount,
    customData: transformCustomData(offer.customData),
    awardRestrictions: awardRestrictionsConstraint
      ? transformAwardRestrictionsToFormValues(awardRestrictionsConstraint)
      : values.awardRestrictions,
  };
};
